import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Le brief lu par l'équipe, sur téléphone, depuis le lien reçu. Deux langues
// au choix, des cases à cocher qui restent cochées sur cet appareil, et rien
// d'autre : ni prix, ni documents, ni factures.

const T = {
  fr: { resume: 'En bref', acces: 'Accès et contact', consignes: 'Consignes', securite: 'Sécurité', pieces: 'Pièce par pièce', etapes: 'Étapes', fournitures: 'Fournitures', ouverts: 'À confirmer avant d’exécuter', plans: 'Plans', imprimer: 'Imprimer', maj: 'Mis à jour le', charge: 'Chargement…', absent: 'Ce lien n’est plus valable. Demandez-en un nouveau au bureau.' },
  ro: { resume: 'Pe scurt', acces: 'Acces și contact', consignes: 'Reguli', securite: 'Siguranță', pieces: 'Cameră cu cameră', etapes: 'Etape', fournitures: 'Materiale', ouverts: 'De confirmat înainte de execuție', plans: 'Planuri', imprimer: 'Printează', maj: 'Actualizat la', charge: 'Se încarcă…', absent: 'Acest link nu mai este valabil. Cereți unul nou de la birou.' },
};

export default function BriefEquipe() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState('chargement');
  const [langue, setLangue] = useState(() => { try { return localStorage.getItem('brief_langue') || 'fr'; } catch { return 'fr'; } });
  const [coches, setCoches] = useState(() => { try { return JSON.parse(localStorage.getItem(`brief_coches_${token}`) || '{}'); } catch { return {}; } });

  useEffect(() => {
    fetch(`/api/brief/${token}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error); setData(d); setEtat('ok'); })
      .catch(() => setEtat('absent'));
  }, [token]);
  useEffect(() => { try { localStorage.setItem('brief_langue', langue); } catch { /* navigation privée */ } }, [langue]);
  const cocher = (cle) => setCoches(c => { const n = { ...c, [cle]: !c[cle] }; try { localStorage.setItem(`brief_coches_${token}`, JSON.stringify(n)); } catch { /* idem */ } return n; });

  const t = T[langue];
  const b = data?.[langue] || data?.fr;

  if (etat !== 'ok' || !b) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-slate-500 text-center">{etat === 'chargement' ? t.charge : t.absent}</div>;
  }

  const Section = ({ titre, children }) => (
    <section className="mb-6"><h2 className="text-xs font-semibold uppercase tracking-wider text-orange-700 border-b border-orange-200 pb-1 mb-3">{titre}</h2>{children}</section>
  );
  const Liste = ({ items }) => <ul className="space-y-1.5 text-[15px] text-slate-800">{(items || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-300">—</span><span>{x}</span></li>)}</ul>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-orange-600 text-white font-black flex items-center justify-center">A</div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{b.titre}</p>
          <p className="text-xs text-slate-500 truncate">{data.ref}{data.adresse ? ` · ${data.adresse}` : ''}{data.dateDebut ? ` · ${data.dateDebut}` : ''}</p>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm font-semibold">
          {['fr', 'ro'].map(l => (
            <button key={l} onClick={() => setLangue(l)} disabled={l === 'ro' && !data.ro}
                    className={`px-3 py-1.5 ${langue === l ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'} disabled:opacity-40`}>{l.toUpperCase()}</button>
          ))}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {b.resume && <Section titre={t.resume}><p className="text-[15px] leading-relaxed">{b.resume}</p></Section>}
        {b.acces && <Section titre={t.acces}><p className="text-[15px] leading-relaxed">{b.acces}</p></Section>}
        {b.securite?.length > 0 && <Section titre={t.securite}><div className="rounded-xl bg-orange-50 border border-orange-200 p-3"><Liste items={b.securite} /></div></Section>}
        {b.consignes?.length > 0 && <Section titre={t.consignes}><Liste items={b.consignes} /></Section>}

        {b.etapes?.length > 0 && (
          <Section titre={t.etapes}>
            <ol className="space-y-3">
              {b.etapes.map((e, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center">{e.ordre ?? i + 1}</span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[15px]">{e.intitule}{e.duree ? <span className="font-normal text-slate-400 text-sm"> · {e.duree}</span> : null}</p>
                    <p className="text-[15px] text-slate-700">{e.contenu}</p>
                    {e.pieces?.length > 0 && <p className="text-xs text-slate-400 mt-0.5">{e.pieces.join(' · ')}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {b.pieces?.length > 0 && (
          <Section titre={t.pieces}>
            <div className="space-y-4">
              {b.pieces.map((p, i) => (
                <div key={i} className="rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
                  <h3 className="font-bold text-lg">{p.nom}</h3>
                  {p.etat && <p className="text-sm text-slate-500 italic mb-2">{p.etat}</p>}
                  <ul className="space-y-2">
                    {(p.travaux || []).map((w, j) => {
                      const cle = `${i}-${j}`;
                      return (
                        <li key={j} className="flex gap-3 items-start">
                          <input type="checkbox" checked={!!coches[cle]} onChange={() => cocher(cle)} className="mt-1 w-5 h-5 rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
                          <div className={coches[cle] ? 'opacity-50 line-through' : ''}>
                            <p className="font-medium text-[15px]">{w.intitule}{w.quantite ? <span className="text-slate-400 font-normal"> — {w.quantite}</span> : null}</p>
                            {w.details && <p className="text-sm text-slate-600">{w.details}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {(p.attention || []).map((a, k) => <p key={k} className="mt-3 text-sm bg-orange-50 border-l-4 border-orange-400 px-3 py-2 rounded-r-lg">{a}</p>)}
                  {p.photos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {p.photos.map(id => <a key={id} href={`/api/photo/${id}`} target="_blank" rel="noreferrer" className="block aspect-[4/3] rounded-lg overflow-hidden bg-slate-100"><img src={`/api/photo/${id}`} alt="" loading="lazy" className="w-full h-full object-cover" /></a>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {data.plans?.length > 0 && (
          <Section titre={t.plans}>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.plans.map((p, i) => <div key={i} className="rounded-xl bg-white border border-slate-200 p-2 overflow-hidden" dangerouslySetInnerHTML={{ __html: p.svg.replace(/width="\d+" height="\d+"/, 'width="100%" height="auto"') }} />)}
            </div>
          </Section>
        )}

        {b.fournitures?.length > 0 && (
          <Section titre={t.fournitures}>
            <table className="w-full text-[15px]"><tbody>
              {b.fournitures.map((f, i) => <tr key={i} className="border-b border-slate-100"><td className="py-1.5 pr-2">{f.designation}</td><td className="py-1.5 text-right whitespace-nowrap text-slate-600">{f.quantite} {f.unite}</td></tr>)}
            </tbody></table>
          </Section>
        )}

        {b.points_ouverts?.length > 0 && <Section titre={t.ouverts}><Liste items={b.points_ouverts} /></Section>}

        <p className="text-xs text-slate-400 mt-8">{t.maj} {new Date(data.modifieLe || data.genereLe).toLocaleDateString(langue === 'ro' ? 'ro-RO' : 'fr-FR')} · Apex Rénovation</p>
        <button onClick={() => window.print()} className="mt-3 text-sm font-semibold text-orange-700 print:hidden">{t.imprimer}</button>
      </main>
    </div>
  );
}
